import React, { useState, useRef, useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const SWIPE_THRESHOLD = 50;

const EventImageCarousel = ({ images = [], altText = '' }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartX = useRef(null);
  const count = images.length;

  const goTo = useCallback((idx) => {
    setActiveIndex(Math.max(0, Math.min(idx, count - 1)));
  }, [count]);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current == null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      goTo(activeIndex + (diff > 0 ? 1 : -1));
    }
    touchStartX.current = null;
  }, [activeIndex, goTo]);

  if (count === 0) return null;

  // Single image - no carousel UI
  if (count === 1) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Box
          component="img"
          src={images[0]}
          alt={altText}
          sx={{
            width: '100%',
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '12px',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)',
            objectFit: 'contain',
            display: 'block',
            margin: '0 auto',
            transition: 'transform 0.3s ease-in-out',
            '&:hover': { transform: 'scale(1.01)' },
          }}
        />
      </Box>
    );
  }

  // Multi-image carousel
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        borderRadius: '12px',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)',
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slides container */}
      <Box
        sx={{
          display: 'flex',
          width: `${count * 100}%`,
          transform: `translateX(-${activeIndex * (100 / count)}%)`,
          transition: 'transform 0.3s ease',
        }}
      >
        {images.map((src, idx) => (
          <Box
            key={idx}
            sx={{
              width: `${100 / count}%`,
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={src}
              alt={`${altText} ${idx + 1}`}
              loading={idx === 0 ? 'eager' : 'lazy'}
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '500px',
                objectFit: 'contain',
                display: 'block',
                margin: '0 auto',
              }}
            />
          </Box>
        ))}
      </Box>

      {/* Left arrow */}
      {activeIndex > 0 && (
        <IconButton
          onClick={() => goTo(activeIndex - 1)}
          sx={{
            position: 'absolute',
            top: '50%',
            left: 8,
            transform: 'translateY(-50%)',
            bgcolor: 'rgba(0,0,0,0.45)',
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
            zIndex: 1,
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}

      {/* Right arrow */}
      {activeIndex < count - 1 && (
        <IconButton
          onClick={() => goTo(activeIndex + 1)}
          sx={{
            position: 'absolute',
            top: '50%',
            right: 8,
            transform: 'translateY(-50%)',
            bgcolor: 'rgba(0,0,0,0.45)',
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
            zIndex: 1,
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}

      {/* Dot indicators */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 1,
        }}
      >
        {images.map((_, idx) => (
          <Box
            key={idx}
            onClick={() => goTo(idx)}
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: idx === activeIndex ? '#fff' : 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
            }}
          />
        ))}
      </Box>

      {/* Image counter */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          bgcolor: 'rgba(0,0,0,0.5)',
          color: '#fff',
          borderRadius: '12px',
          px: 1.5,
          py: 0.5,
          fontSize: '0.85rem',
        }}
      >
        {activeIndex + 1} / {count}
      </Box>
    </Box>
  );
};

export default React.memo(EventImageCarousel);
